import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import ContactForm from '../../components/common/ContactForm/ContactForm';
import SiridhariniBlogService from '../../services/siridhariniBlogService';

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [blogCategories, setBlogCategories] = useState([]);

  // Load blogs and metadata on component mount
  useEffect(() => {
    loadBlogsAndMetadata();
  }, []);

  const loadBlogsAndMetadata = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load blogs and categories in parallel
      const [blogs, categoriesData] = await Promise.all([
        SiridhariniBlogService.getAllPublishedBlogs(),
        SiridhariniBlogService.getAllCategories()
      ]);

      setBlogPosts(blogs);
      setBlogCategories(categoriesData);
      
      console.log('📚 Loaded', blogs.length, 'blogs and', categoriesData.length, 'categories');
    } catch (error) {
      console.error('❌ Error loading blogs:', error);
      setError('Failed to load blogs. Please try again later.');
      
      // Fallback to empty state
      setBlogPosts([]);
      setBlogCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (searchValue) => {
    if (!searchValue.trim()) {
      // If search is cleared, reload all blogs
      await loadBlogsAndMetadata();
      return;
    }

    try {
      setLoading(true);
      const searchResults = await SiridhariniBlogService.searchBlogs(searchValue);
      setBlogPosts(searchResults);
    } catch (error) {
      console.error('❌ Error searching blogs:', error);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle category filter
  const handleCategoryFilter = async (category) => {
    setSelectedCategory(category);
    
    if (!category) {
      // If no category selected, load all blogs
      await loadBlogsAndMetadata();
      return;
    }

    try {
      setLoading(true);
      const categoryBlogs = await SiridhariniBlogService.getBlogsByCategory(category);
      setBlogPosts(categoryBlogs);
    } catch (error) {
      console.error('❌ Error filtering by category:', error);
      setError('Category filter failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Transform blog data for display
  const transformBlogForDisplay = (blog) => {
    // Use featured image first, then first content image, no fallback
    let displayImage = blog.featuredImageUrl;
    if (!displayImage && blog.images && blog.images.length > 0) {
      displayImage = blog.images[0];
    }
    // No fallback - let displayImage be null/undefined if no image exists

    return {
      id: blog.id,
      title: blog.title,
      excerpt: blog.excerpt || SiridhariniBlogService.truncateText(blog.content, 200),
      date: SiridhariniBlogService.formatDate(blog.createdAt || blog.publishedAt),
      comments: "No Comments",
      image: displayImage, // Can be null/undefined
      featured: blog.isFeatured,
      color: SiridhariniBlogService.getCategoryColor(blog.category),
      category: blog.category,
      readTime: blog.readTime,
      author: blog.author,
      slug: blog.slug,
      images: blog.images || [] // Include all images for potential future use
    };
  };

  // Recent posts based on dynamic data
  const recentPosts = blogPosts.slice(0, 5).map(blog => blog.title);

  // Categories from dynamic data
  const categories = blogCategories;

  const archives = [
    "November 2021",
    "August 2021",
    "December 2018"
  ];

  const articleTags = [
    "AGRO", "CROP", "CULTIVATION", "ECO", "ENVIRONMENT", "FARMING", "FOOD",
    "GREEN", "HARVESTING", "HORTICULTURE", "PROFITS", "REALTY", "SANDALWOOD",
    "TOUCHSTONE", "WEALTH"
  ];

  const calendarData = {
    month: "October 2025",
    days: [
      { date: 1, day: "W" }, { date: 2, day: "T" }, { date: 3, day: "F" }, { date: 4, day: "S" }, { date: 5, day: "S" },
      { date: 6, day: "M" }, { date: 7, day: "T" }, { date: 8, day: "W" }, { date: 9, day: "T" }, { date: 10, day: "F" },
      { date: 11, day: "S" }, { date: 12, day: "S" }, { date: 13, day: "M" }, { date: 14, day: "T" }, { date: 15, day: "W" },
      { date: 16, day: "T" }, { date: 17, day: "F" }, { date: 18, day: "S" }, { date: 19, day: "S" }, { date: 20, day: "M" },
      { date: 21, day: "T" }, { date: 22, day: "W" }, { date: 23, day: "T" }, { date: 24, day: "F" }, { date: 25, day: "S" },
      { date: 26, day: "S" }, { date: 27, day: "M" }, { date: 28, day: "T" }, { date: 29, day: "W" }, { date: 30, day: "T" },
      { date: 31, day: "F" }
    ]
  };

  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    },
    heroSection: {
      backgroundImage: 'url("https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&h=800")',
      backgroundColor: '#4A5D23',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative',
      color: 'white',
      padding: '200px 0 150px 0',
      minHeight: '80vh',
      textAlign: 'left'
    },
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.4)'
    },
    heroContent: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    heroLabel: {
      fontSize: '1.2rem',
      fontWeight: '400',
      marginBottom: '1rem',
      textTransform: 'uppercase',
      letterSpacing: '0.1rem'
    },
    heroTitle: {
      fontSize: '4rem',
      fontWeight: '700',
      textTransform: 'uppercase'
    },
    blogSection: {
      padding: '80px 0',
      backgroundColor: '#ffffff'
    },
    sectionContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    blogLayout: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: '4rem',
      marginTop: '3rem'
    },
    mainContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '3rem'
    },
    blogPost: {
      backgroundColor: 'white',
      borderRadius: '0',
      overflow: 'hidden',
      borderBottom: '1px solid #e5e5e5',
      paddingBottom: '3rem'
    },
    blogImage: {
      width: '100%',
      height: '300px',
      objectFit: 'cover',
      marginBottom: '2rem'
    },
    blogMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      color: '#2d5016'
    },
    metaIcon: {
      width: '12px',
      height: '12px',
      backgroundColor: '#2d5016',
      borderRadius: '50%'
    },
    blogTitle: {
      fontSize: '1.8rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
      color: '#333',
      textTransform: 'uppercase'
    },
    blogExcerpt: {
      fontSize: '1rem',
      color: '#666',
      marginBottom: '2rem',
      lineHeight: '1.8'
    },
    readMoreBtn: {
      padding: '0.8rem 2rem',
      border: '2px solid #333',
      backgroundColor: 'transparent',
      color: '#333',
      borderRadius: '25px',
      fontSize: '0.9rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    sidebar: {
      display: 'flex',
      flexDirection: 'column',
      gap: '3rem'
    },
    sidebarWidget: {
      backgroundColor: '#f8f9fa',
      padding: '2rem',
      borderRadius: '0'
    },
    widgetTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '1.5rem',
      textTransform: 'uppercase'
    },
    searchBox: {
      width: '100%',
      padding: '1rem',
      border: '1px solid #ddd',
      borderRadius: '0',
      fontSize: '1rem'
    },
    searchBtn: {
      marginTop: '1rem',
      padding: '0.8rem 2rem',
      backgroundColor: '#333',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    listItem: {
      padding: '0.5rem 0',
      borderBottom: '1px solid #eee',
      fontSize: '0.9rem'
    },
    listItemLast: {
      borderBottom: 'none'
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem'
    },
    tag: {
      padding: '0.4rem 1rem',
      backgroundColor: '#333',
      color: 'white',
      borderRadius: '15px',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      fontWeight: '500'
    },
    contactSection: {
      padding: '6rem 0',
      background: 'linear-gradient(135deg, #E67E22 0%, #D35400 100%)',
      color: 'white',
      textAlign: 'center'
    },
    contactTitle: {
      fontSize: '3rem',
      fontWeight: '700',
      margin: '0 0 3rem 0',
      textTransform: 'uppercase'
    },
    contactGrid: {
      maxWidth: '800px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    inputLabel: {
      fontSize: '1rem',
      marginBottom: '0.5rem',
      textAlign: 'left',
      opacity: '0.9'
    },
    input: {
      padding: '1rem 0',
      border: 'none',
      borderBottom: '2px solid rgba(255,255,255,0.3)',
      backgroundColor: 'transparent',
      color: 'white',
      fontSize: '1rem',
      outline: 'none'
    },
    textareaGroup: {
      gridColumn: 'span 2',
      display: 'flex',
      flexDirection: 'column'
    },
    textarea: {
      padding: '1rem 0',
      border: 'none',
      borderBottom: '2px solid rgba(255,255,255,0.3)',
      backgroundColor: 'transparent',
      color: 'white',
      fontSize: '1rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical'
    },
    sendButton: {
      gridColumn: 'span 2',
      marginTop: '2rem',
      padding: '1rem 3rem',
      backgroundColor: 'transparent',
      border: '2px solid white',
      color: 'white',
      borderRadius: '25px',
      fontSize: '1.1rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    calendar: {
      backgroundColor: '#333',
      color: 'white',
      padding: '1rem',
      borderRadius: '0'
    },
    calendarHeader: {
      textAlign: 'center',
      fontSize: '1rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
      textTransform: 'uppercase'
    },
    calendarGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '0.5rem',
      fontSize: '0.9rem'
    },
    calendarDayHeader: {
      textAlign: 'center',
      fontWeight: 'bold',
      padding: '0.5rem 0',
      fontSize: '0.8rem'
    },
    calendarDay: {
      textAlign: 'center',
      padding: '0.5rem',
      cursor: 'pointer'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '3rem',
      paddingTop: '2rem',
      borderTop: '1px solid #e5e5e5'
    },
    pageNumber: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      border: '2px solid #333',
      backgroundColor: 'transparent',
      color: '#333',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'all 0.3s ease'
    },
    activePageNumber: {
      backgroundColor: '#E67E22',
      borderColor: '#E67E22',
      color: 'white'
    },
    recentComments: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '1.5rem',
      textTransform: 'uppercase'
    },
    findButton: {
      position: 'fixed',
      bottom: '2rem',
      right: '2rem',
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      backgroundColor: '#333',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
      zIndex: 1000
    },
    featuredPost: {
      position: 'relative'
    },
    featuredBadge: {
      position: 'absolute',
      top: '1rem',
      left: '1rem',
      backgroundColor: '#E67E22',
      color: 'white',
      padding: '0.5rem 1rem',
      borderRadius: '15px',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      zIndex: 2
    }
  };

  return (
    <>
      <style>{`
        .read-more-btn:hover {
          background-color: #333;
          color: white;
        }
        
        .send-btn:hover {
          background-color: white;
          color: #E67E22;
        }
        
        .input::placeholder,
        .textarea::placeholder {
          color: rgba(255,255,255,0.7);
        }
        
        .input:focus,
        .textarea:focus {
          border-bottom-color: white;
        }
        
        @media (max-width: 768px) {
          .blog-layout {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
          .hero-title {
            font-size: 2.5rem !important;
          }
        }
      `}</style>
      
      <div style={styles.container}>
        <Header />
        
        {/* Hero Section */}
        <section style={styles.heroSection}>
          <div style={styles.heroOverlay}></div>
          <div style={styles.heroContent}>
            <div style={styles.heroLabel}>OUR</div>
            <h1 style={styles.heroTitle} className="hero-title">BLOG</h1>
          </div>
        </section>

        {/* Blog Posts */}
        <section style={styles.blogSection}>
          <div style={styles.sectionContainer}>
            <div style={styles.blogLayout} className="blog-layout">
              {/* Main Content */}
              <div style={styles.mainContent}>
                {/* Loading State */}
                {loading && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '4rem 0', 
                    fontSize: '1.2rem', 
                    color: '#666' 
                  }}>
                    Loading blogs...
                  </div>
                )}

                {/* Error State */}
                {error && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '4rem 0', 
                    fontSize: '1.2rem', 
                    color: '#dc3545',
                    background: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '8px',
                    marginBottom: '2rem'
                  }}>
                    {error}
                    <br />
                    <button 
                      onClick={loadBlogsAndMetadata}
                      style={{
                        marginTop: '1rem',
                        padding: '0.5rem 1rem',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* No Blogs State */}
                {!loading && !error && blogPosts.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '4rem 0', 
                    fontSize: '1.2rem', 
                    color: '#666' 
                  }}>
                    No blogs found. {searchTerm || selectedCategory ? 
                      'Try adjusting your search or category filter.' : 
                      'Check back later for new content!'}
                  </div>
                )}

                {/* Blog Posts */}
                {!loading && !error && blogPosts.map((post) => {
                  const displayPost = transformBlogForDisplay(post);
                  return (
                    <article key={displayPost.id} style={{...styles.blogPost, ...(displayPost.featured ? styles.featuredPost : {})}}>
                      {displayPost.featured && (
                        <div style={styles.featuredBadge}>FEATURED</div>
                      )}
                      {displayPost.image && (
                        <img 
                          src={displayPost.image} 
                          alt={displayPost.title}
                          style={styles.blogImage}
                          onError={(e) => {
                            // Hide broken images
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div style={styles.blogMeta}>
                        <div style={styles.metaItem}>
                          <div style={styles.metaIcon}></div>
                          {displayPost.comments}
                        </div>
                        <div style={styles.metaItem}>
                          <div style={styles.metaIcon}></div>
                          {displayPost.date}
                        </div>
                        {displayPost.readTime && (
                          <div style={styles.metaItem}>
                            <div style={styles.metaIcon}></div>
                            {displayPost.readTime}
                          </div>
                        )}
                        {displayPost.category && (
                          <div style={{
                            ...styles.metaItem,
                            background: displayPost.color,
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.8rem'
                          }}>
                            {displayPost.category}
                          </div>
                        )}
                      </div>
                      <h2 style={{...styles.blogTitle, ...(displayPost.featured ? {color: displayPost.color} : {})}}>{displayPost.title}</h2>
                      <p style={styles.blogExcerpt}>{displayPost.excerpt}</p>
                      <button 
                        style={styles.readMoreBtn} 
                        className="read-more-btn"
                        onClick={() => navigate(displayPost.slug ? `/blogs/${displayPost.slug}` : `/blogs/${displayPost.id}`)}
                      >
                        READ MORE
                      </button>
                    </article>
                  );
                })}
                
                {/* Pagination - Keep simple for now, can be enhanced later */}
                {!loading && !error && blogPosts.length > 0 && (
                  <div style={styles.pagination}>
                    <div style={{...styles.pageNumber, ...styles.activePageNumber}}>1</div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside style={styles.sidebar}>
                {/* Search */}
                <div style={styles.sidebarWidget}>
                  <h3 style={styles.widgetTitle}>Search</h3>
                  <input 
                    type="text" 
                    style={styles.searchBox} 
                    placeholder="Search blogs..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchTerm)}
                  />
                  <button 
                    style={styles.searchBtn}
                    onClick={() => handleSearch(searchTerm)}
                  >
                    Search
                  </button>
                </div>

                {/* Recent Posts */}
                <div style={styles.sidebarWidget}>
                  <h3 style={styles.widgetTitle}>Recent Posts</h3>
                  {recentPosts.map((post, index) => (
                    <div key={index} style={{...styles.listItem, ...(index === recentPosts.length - 1 ? styles.listItemLast : {})}}>
                      {post}
                    </div>
                  ))}
                </div>

                {/* Archives */}
                <div style={styles.sidebarWidget}>
                  <h3 style={styles.widgetTitle}>Archives</h3>
                  {archives.map((archive, index) => (
                    <div key={index} style={{...styles.listItem, ...(index === archives.length - 1 ? styles.listItemLast : {})}}>
                      {archive}
                    </div>
                  ))}
                </div>

                {/* Categories */}
                <div style={styles.sidebarWidget}>
                  <h3 style={styles.widgetTitle}>Categories</h3>
                  {categories.map((category, index) => (
                    <div key={index} style={{...styles.listItem, ...(index === categories.length - 1 ? styles.listItemLast : {})}}>
                      {category}
                    </div>
                  ))}
                </div>

                {/* Recent Comments */}
                <div style={styles.sidebarWidget}>
                  <h3 style={styles.recentComments}>Recent Comments</h3>
                </div>

                {/* Calendar Widget */}
                <div style={styles.calendar}>
                  <h3 style={styles.calendarHeader}>{calendarData.month}</h3>
                  <div style={styles.calendarGrid}>
                    <div style={styles.calendarDayHeader}>M</div>
                    <div style={styles.calendarDayHeader}>T</div>
                    <div style={styles.calendarDayHeader}>W</div>
                    <div style={styles.calendarDayHeader}>T</div>
                    <div style={styles.calendarDayHeader}>F</div>
                    <div style={styles.calendarDayHeader}>S</div>
                    <div style={styles.calendarDayHeader}>S</div>
                    
                    {calendarData.days.map((day, index) => (
                      <div key={index} style={styles.calendarDay}>{day.date}</div>
                    ))}
                  </div>
                  <div style={{textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem'}}>
                    « Nov
                  </div>
                </div>

                {/* Article Tags */}
                <div style={styles.sidebarWidget}>
                  <h3 style={styles.widgetTitle}>Article Tags</h3>
                  <div style={styles.tagsContainer}>
                    {articleTags.map((tag, index) => (
                      <span key={index} style={styles.tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <ContactForm 
          pageSource="Blogs Page"
          title="Get In Touch"
          onSubmitSuccess={(result) => {
            console.log('Blogs page contact form submitted successfully:', result);
          }}
          onSubmitError={(error) => {
            console.error('Blogs page contact form submission failed:', error);
          }}
        />

        {/* Fixed FIND Button */}
        <button style={styles.findButton}>
          FIND
        </button>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;